import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shirt, Layers, Rocket, CheckCircle2, Sparkles } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { BUSINESS_NAME, BANNER_URL, IMG_PLAIN_TSHIRT, IMG_POD } from "../lib/site";

const SERVICES = [
  {
    to: "/premiumtshirtmanufacturing",
    icon: Shirt,
    img: IMG_PLAIN_TSHIRT,
    tag: "Wholesale Manufacturing",
    title: "Buy Plain T-Shirts",
    desc: "Premium t-shirt manufacturer. Customisation possible. 100 pcs MOQ.",
    points: ["Multiple fabrics & colours", "Sizes S – 3XL", "MOQ 100 pcs"],
    cta: "View Catalog & Pricing",
    shadow: "rgba(15,23,42,1)",
    testid: "service-tshirt",
  },
  {
    to: "/dtf",
    icon: Layers,
    img: BANNER_URL,
    tag: "India's Most Premium DTF",
    title: "Buy DTF Gang Sheets",
    desc: "Vibrant colours, 80+ wash guarantee, soft hand-feel. Customise your designs or pick from our 100+ ready-to-print designs.",
    points: ["80+ wash guarantee", "Soft hand-feel prints", "100+ ready designs"],
    cta: "Estimate Price & Order",
    shadow: "rgba(255,212,0,0.7)",
    testid: "service-dtf",
  },
  {
    to: "/POD",
    icon: Rocket,
    img: IMG_POD,
    tag: "Start With ₹0 Investment",
    title: "Print On Demand",
    desc: "Start your clothing brand with 0 investment. We handle printing, stock & shipping — you focus on sales.",
    points: ["0 setup cost", "0 inventory cost", "Pay only on shipping"],
    cta: "Launch Your Brand",
    shadow: "rgba(15,23,42,1)",
    testid: "service-pod",
  },
];

export default function LandingSelector() {
  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-slate-900 selection:text-white">
      <SiteHeader active="/" />

      {/* Hero */}
      <section className="relative py-16 sm:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 border border-slate-200 text-xs font-mono tracking-wider uppercase text-slate-700">
            <Sparkles className="w-3.5 h-3.5 text-slate-900" /> One Stop Apparel Partner
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.2] uppercase">
            What Would You Like To <span className="bg-[#FFD400] text-slate-950 px-2 box-decoration-clone">Order Today?</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto font-medium">
            Welcome to <strong className="text-slate-950">{BUSINESS_NAME}</strong>. Choose a service below to get started — wholesale t-shirts, premium DTF transfers, or launch your own brand with Print-on-Demand.
          </p>
        </div>
      </section>

      {/* Service selector cards */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  data-testid={s.testid}
                  className="group bg-white border-2 border-slate-950 flex flex-col transition-all duration-150 hover:-translate-y-1"
                  style={{ boxShadow: `6px 6px 0px 0px ${s.shadow}` }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-slate-950 bg-slate-100">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <span className="absolute top-3 left-3 bg-slate-950 text-white text-[10px] font-mono tracking-widest uppercase px-2.5 py-1">
                      {s.tag}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-slate-950 text-white flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <h2 className="text-xl font-extrabold uppercase tracking-tight leading-tight">{s.title}</h2>
                    </div>
                    <p className="text-sm text-slate-600 font-medium leading-relaxed">{s.desc}</p>
                    <ul className="space-y-1.5 pt-1">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-slate-950 shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-4">
                      <span className="inline-flex items-center gap-2 bg-[#FFD400] text-slate-950 group-hover:bg-[#ffdb33] text-xs font-bold uppercase tracking-wider px-5 py-3 w-full justify-center transition-colors border border-slate-950">
                        {s.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
